"use client";

import { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import Footer from "@/components/Footer";
import ModalMount from "@/components/modals/ModalMount";
import { ThemeProvider } from "@/components/ThemeProvider";
import OverlayGuard from "@/components/OverlayGuard";
import DevOnlyNavRecorderLoader from "@/components/DevOnlyNavRecorderLoader";
import DebugToolsClient from "@/components/debug/DebugToolsClient";
import SafariLayersDebug from "@/components/debug/SafariLayersDebug";
import StallRecorderClient from "@/components/debug/StallRecorderClient";
import RscLoopDiagClient from "@/components/debug/RscLoopDiagClient";
import ThemeDiagnostics from "@/components/debug/ThemeDiagnostics";
import SafariNavGuardClient from "@/components/nav/SafariNavGuardClient";
import SafariDesktopClassClient from "@/components/SafariDesktopClassClient";
import CrashLoggerClient from "@/components/CrashLoggerClient";
import {
  ChunkErrorBoundary,
  ChunkErrorRecoveryListener,
} from "@/components/ChunkErrorRecovery";
import WebVitalsReporter from "@/components/WebVitalsReporter";
import { AuthProvider } from "@/components/AuthProvider";
import ScrollToTop from "@/components/ScrollToTop";
import { CartProvider } from "@/components/cart/CartProvider";
import { LocationProvider } from "@/components/location/LocationProvider";
import UrlLocationMigratorClient from "@/components/location/UrlLocationMigratorClient";
import RealtimeProvider from "@/components/realtime/RealtimeProvider";
import AutoRefreshGuardBanner from "@/components/auth/AutoRefreshGuardBanner";
import HomepageLoadingShell from "@/components/home/HomepageLoadingShell";
import { logPostLoginNavigationResult } from "@/lib/auth/postLoginNavigationDiagnostics";
import FeedbackProvider from "@/components/feedback/FeedbackProvider";
import CustomerSupportUnreadBoundary from "@/components/support/CustomerSupportUnreadBoundary";

export default function AppShell({
  children,
  initialLocation = null,
  initialAuth = null,
  initialAuthResolved: initialAuthResolvedProp,
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isAuthHandoff = searchParams?.get("yb_auth_handoff") === "1";
  const initialAuthResolved =
    typeof initialAuthResolvedProp === "boolean"
      ? initialAuthResolvedProp
      : Boolean(initialAuth?.user?.id) || !isAuthHandoff;
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_AUTH_DIAG !== "1") return;
    console.info("[AUTH_CLIENT_BOOTSTRAP]", {
      source: "AppShell",
      pathname,
      hasInitialUser: Boolean(initialAuth?.user?.id),
      initialRole: initialAuth?.role || null,
      hasInitialProfile: Boolean(initialAuth?.profile?.id),
      isAuthHandoff,
      initialAuthResolved,
    });
  }, [
    initialAuth?.profile?.id,
    initialAuth?.role,
    initialAuth?.user?.id,
    initialAuthResolved,
    isAuthHandoff,
    pathname,
  ]);
  useEffect(() => {
    logPostLoginNavigationResult();
  }, [pathname, searchParams]);
  const flushFooterOnHome = pathname === "/" || pathname === "/customer/home";
  const flushFooterOnPublicListings = pathname === "/listings";
  const flushFooterOnPublicBusinessProfile = pathname?.startsWith("/b/");
  const flushFooterOnAuth =
    pathname === "/login" ||
    pathname === "/signin" ||
    pathname === "/set-password" ||
    pathname === "/oauth/callback" ||
    pathname === "/business/login" ||
    pathname?.startsWith("/auth/") ||
    pathname?.startsWith("/business-auth/") ||
    pathname?.startsWith("/staff/invite/");
  const isOnboardingRoute =
    pathname === "/onboarding" ||
    pathname === "/business/onboarding" ||
    pathname?.startsWith("/business/onboarding/");
  const flushFooterOnBusiness =
    pathname === "/onboarding" ||
    pathname === "/business/onboarding" ||
    pathname?.startsWith("/business/onboarding/") ||
    pathname === "/business" ||
    pathname?.startsWith("/business/");
  const isAdminRoute = pathname === "/admin" || pathname.startsWith("/admin/");
  const shellKind = isOnboardingRoute ? "onboarding" : isAdminRoute ? "admin" : "default";
  const providerFallback = pathname === "/" ? <HomepageLoadingShell /> : null;
  const lightThemeVars = {
    "--yb-bg": "#f6f7fb",
    "--color-bg": "#f6f7fb",
    "--background": "#f6f7fb",
    "--bg-solid": "#f6f7fb",
    "--bg-gradient-start": "#f6f7fb",
    "--bg-gradient-end": "#f6f7fb",
    "--glow-1": "transparent",
    "--glow-2": "transparent",
  };

  return (
    <div
      className="app-shell-root relative min-h-screen overflow-x-hidden w-full antialiased text-[var(--yb-text)] flex flex-col bg-[#f6f7fb]"
      style={lightThemeVars}
      data-theme-root="1"
      data-theme="light"
      data-shell-kind={shellKind}
    >
      <ChunkErrorRecoveryListener />
      <CrashLoggerClient />
      <RscLoopDiagClient />
      <WebVitalsReporter />
      <ScrollToTop />
      <DevOnlyNavRecorderLoader />
      <ThemeProvider forcedTheme="light">
        <OverlayGuard />
        <div className="absolute inset-0 -z-10 overflow-hidden h-full">
          <div
            className="app-shell-bg-solid absolute inset-0"
            style={{ background: "var(--bg-solid)" }}
          />
          <div
            className="app-shell-bg-gradient absolute inset-0"
            style={{
              background: "linear-gradient(to bottom, var(--bg-gradient-start), var(--bg-gradient-end))",
            }}
          />
          <div className="app-shell-glow pointer-events-none absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full blur-[120px] bg-[var(--glow-1)]" />
          <div className="app-shell-glow pointer-events-none absolute top-40 -right-24 h-[480px] w-[480px] rounded-full blur-[120px] bg-[var(--glow-2)]" />
          <div className="animated-bg" />
        </div>

        {/* Single provider tree; LocationProvider is the source of truth for city/ZIP. */}
        <ChunkErrorBoundary>
          <Suspense fallback={providerFallback}>
            <LocationProvider initialLocation={initialLocation}>
              <UrlLocationMigratorClient />
              <AuthProvider
                initialUser={initialAuth?.user ?? null}
                initialProfile={initialAuth?.profile ?? null}
                initialAccountContext={initialAuth?.accountContext ?? null}
                initialRole={initialAuth?.role ?? null}
                initialAuthResolved={initialAuthResolved}
              >
                <FeedbackProvider>
                  <AutoRefreshGuardBanner />
                  <RealtimeProvider>
                    <CartProvider>
                      <CustomerSupportUnreadBoundary>
                      <ModalMount>
                      <main
                        className="flex-1 w-full min-h-screen"
                        style={{ paddingTop: "0px" }}
                        data-app-shell-main="1"
                        data-shell-kind={shellKind}
                      >
                        {children}
                      </main>
                      {!isAdminRoute && !isOnboardingRoute ? (
                        <Footer
                          className={
                            flushFooterOnAuth
                              ? "mt-0"
                              : flushFooterOnHome
                              ? "mt-0 border-t-0"
                              : flushFooterOnPublicListings
                                ? "mt-0"
                                : flushFooterOnPublicBusinessProfile
                                  ? "mt-0"
                              : flushFooterOnBusiness
                                ? "mt-0"
                                : undefined
                          }
                        />
                      ) : null}
                      </ModalMount>
                      </CustomerSupportUnreadBoundary>
                    </CartProvider>
                  </RealtimeProvider>
                </FeedbackProvider>
              </AuthProvider>
            </LocationProvider>
          </Suspense>
        </ChunkErrorBoundary>
      </ThemeProvider>
      <DebugToolsClient />
      <ThemeDiagnostics />
      <SafariLayersDebug />
      <StallRecorderClient />
      <SafariNavGuardClient />
      <SafariDesktopClassClient />
    </div>
  );
}
